import React, { useState } from 'react';
import styled from 'styled-components';
import Icon from '@mdi/react';
import {
  mdiPump,
  mdiPipeDisconnected,
  mdiSourceBranch,
  mdiSourceMerge,
  mdiPipe,
  mdiFire,
  mdiSnowflake,
  mdiLabelOutline,
  mdiCommentTextOutline,
} from '@mdi/js';
import { ComponentType } from '../types';

const LibraryContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
`;

const ElementItem = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 10px;
  margin: 2px 0;
  border-radius: 4px;
  cursor: grab;
  background-color: #ffffff;
  border: 1px solid transparent;
  transition: all 0.15s ease;

  &:hover {
    background-color: #f8f9fa;
    border-color: #e0e0e0;
  }

  &:active {
    cursor: grabbing;
  }
`;

const ElementIconWrapper = styled.div<{ color: string }>`
  width: 28px;
  height: 28px;
  background-color: ${props => props.color};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;
`;

const ElementInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ElementName = styled.div`
  font-weight: 500;
  font-size: 13px;
  color: #202124;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ElementDescription = styled.div`
  font-size: 11px;
  color: #9aa0a6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;


const ICON_MAP: Record<string, string> = {
  pump: mdiPump,
  pipe: mdiPipeDisconnected,
  branch: mdiSourceBranch,
  merge: mdiSourceMerge,
  restriction: mdiPipe,
  heat_source: mdiFire,
  cooling_package: mdiSnowflake,
};

const COLOR_MAP: Record<string, string> = {
  pump: '#1a73e8',
  pipe: '#5f6368',
  branch: '#34a853',
  merge: '#34a853',
  restriction: '#ea8600',
  heat_source: '#d93025',
  cooling_package: '#1a73e8',
};

const GENERIC_COMPONENTS: ComponentType[] = [
  {
    id: 0, name: 'Pump', component_type: 'pump', component_type_display: 'Pump',
    description: 'Circulates coolant', icon: 'pump', input_ports: 1, output_ports: 1,
    default_properties: { fixed_flow_lpm: 30 }, created_at: '', updated_at: '',
  },
  {
    id: 0, name: 'Pipe', component_type: 'pipe', component_type_display: 'Pipe',
    description: 'Connects components', icon: 'pipe', input_ports: 1, output_ports: 1,
    default_properties: { inner_diameter_mm: 20, length_m: 1 }, created_at: '', updated_at: '',
  },
  {
    id: 0, name: 'Branch', component_type: 'branch', component_type_display: 'Branch',
    description: 'Splits flow into branches', icon: 'branch', input_ports: 1, output_ports: 2,
    default_properties: {}, created_at: '', updated_at: '',
  },
  {
    id: 0, name: 'Merge', component_type: 'merge', component_type_display: 'Merge',
    description: 'Merges flow paths', icon: 'merge', input_ports: 2, output_ports: 1,
    default_properties: {}, created_at: '', updated_at: '',
  },
  {
    id: 0, name: 'Restriction', component_type: 'restriction', component_type_display: 'Restriction',
    description: 'Pressure drop element', icon: 'restriction', input_ports: 1, output_ports: 1,
    default_properties: { k_factor: 1 }, created_at: '', updated_at: '',
  },
  {
    id: 0, name: 'Heat Source', component_type: 'heat_source', component_type_display: 'Heat Source',
    description: 'Adds thermal load', icon: 'heat_source', input_ports: 1, output_ports: 1,
    default_properties: { heat_input_kw: 5 }, created_at: '', updated_at: '',
  },
  {
    id: 0, name: 'Cooling Package', component_type: 'cooling_package', component_type_display: 'Cooling Package',
    description: 'Removes heat from coolant', icon: 'cooling_package', input_ports: 1, output_ports: 1,
    default_properties: {}, created_at: '', updated_at: '',
  },
];

interface DraggableElementProps {
  element: ComponentType;
}

const DraggableElement: React.FC<DraggableElementProps> = ({ element }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true);
    e.dataTransfer.setData('application/json', JSON.stringify(element));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const iconPath = ICON_MAP[element.component_type] || mdiPipe;
  const iconColor = COLOR_MAP[element.component_type] || '#5f6368';

  return (
    <ElementItem
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <ElementIconWrapper color={iconColor}>
        <Icon path={iconPath} size={0.65} color="white" />
      </ElementIconWrapper>
      <ElementInfo>
        <ElementName>{element.name}</ElementName>
        <ElementDescription>{element.description}</ElementDescription>
      </ElementInfo>
    </ElementItem>
  );
};

const SectionDivider = styled.div`
  height: 1px;
  background: #e8eaed;
  margin: 8px 0;
`;

const SectionTitle = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #9aa0a6;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 4px 10px 2px;
`;

const DraggableAnnotation: React.FC<{ type: 'label' | 'comment' }> = ({ type }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true);
    e.dataTransfer.setData('application/annotation', type);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const isLabel = type === 'label';

  return (
    <ElementItem
      draggable
      onDragStart={handleDragStart}
      onDragEnd={() => setIsDragging(false)}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <ElementIconWrapper color="#9aa0a6">
        <Icon path={isLabel ? mdiLabelOutline : mdiCommentTextOutline} size={0.65} color="white" />
      </ElementIconWrapper>
      <ElementInfo>
        <ElementName>{isLabel ? 'Label' : 'Comment'}</ElementName>
        <ElementDescription>{isLabel ? 'Add text label to canvas' : 'Add comment note to canvas'}</ElementDescription>
      </ElementInfo>
    </ElementItem>
  );
};

export const ElementsLibrary: React.FC = () => {
  return (
    <LibraryContainer>
      {GENERIC_COMPONENTS.map((component, index) => (
        <DraggableElement key={`${component.component_type}-${index}`} element={component} />
      ))}

      <SectionDivider />
      <SectionTitle>Annotations</SectionTitle>
      <DraggableAnnotation type="label" />
      <DraggableAnnotation type="comment" />
    </LibraryContainer>
  );
};
